// @ts-nocheck
import { useState, useRef } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { RichTextEditor } from "@/design-system/forms/RichTextEditor";
import { EventData, Sport, EventType, EventCategory } from "@/types/events";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
  sports: Sport[];
  eventTypes: EventType[];
  categories: EventCategory[];
}

export function BasicInfoSection({ event, updateEvent, sports, eventTypes, categories }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverFileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [localCategories, setLocalCategories] = useState<EventCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  const allCategories = [...categories, ...localCategories];

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setAddingCategory(true);
    try {
      const { data, error } = await (supabase as any)
        .from("event_categories")
        .insert({ name })
        .select()
        .single();
      if (error) throw error;
      setLocalCategories((prev) => [...prev, data]);
      updateEvent({ event_category_ids: [...(event.event_category_ids ?? []), data.id] });
      setNewCategoryName("");
      toast.success(`Added "${name}"`);
    } catch (err: any) {
      toast.error(`Failed to add category: ${err.message}`);
    } finally {
      setAddingCategory(false);
    }
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `thumbnails/${event.id ?? "new"}-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage
        .from("event-assets")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) {
        console.error("Thumbnail upload error:", error);
        toast.error(`Upload failed: ${error.message}`);
        return;
      }
      const { data: urlData } = supabase.storage.from("event-assets").getPublicUrl(data.path);
      updateEvent({ thumbnail_url: urlData.publicUrl });
      toast.success("Image uploaded");
    } catch (err: any) {
      console.error(err);
      toast.error(`Upload failed: ${err.message ?? err}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File must be under 8MB");
      return;
    }
    setUploadingCover(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `covers/${event.id ?? "new"}-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage
        .from("event-assets")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) {
        console.error("Cover upload error:", error);
        toast.error(`Upload failed: ${error.message}`);
        return;
      }
      const { data: urlData } = supabase.storage.from("event-assets").getPublicUrl(data.path);
      updateEvent({ cover_photo_url: urlData.publicUrl } as any);
      toast.success("Cover photo uploaded");
    } catch (err: any) {
      console.error(err);
      toast.error(`Upload failed: ${err.message ?? err}`);
    } finally {
      setUploadingCover(false);
      if (coverFileInputRef.current) coverFileInputRef.current.value = "";
    }
  };

  const toggleCategory = (catId: string) => {
    const current = event.event_category_ids ?? [];
    if (current.includes(catId)) {
      updateEvent({ event_category_ids: current.filter((id) => id !== catId) });
    } else {
      updateEvent({ event_category_ids: [...current, catId] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Main fields */}
      <div className="space-y-4">
        <div>
          <FieldLabel required>Event Title</FieldLabel>
          <input
            type="text"
            value={event.name}
            onChange={(e) => updateEvent({ name: e.target.value })}
            placeholder="e.g., Summer Basketball Classic"
            className={inputClass}
          />
        </div>

        <div>
          <FieldLabel>Gender</FieldLabel>
          <select
            value={
              event.gender === "boys"
                ? "Men"
                : event.gender === "girls"
                  ? "Women"
                  : event.gender === "coed"
                    ? "Co-ed"
                    : event.gender || ""
            }
            onChange={(e) => updateEvent({ gender: e.target.value })}
            className={inputClass}
          >
            <option value="">Select...</option>
            <option value="Men">Men</option>
            <option value="Women">Women</option>
            <option value="Co-ed">Co-ed</option>
          </select>
        </div>

        <div>
          <FieldLabel>Event Description</FieldLabel>
          <RichTextEditor
            value={event.description || ""}
            onChange={(html) => updateEvent({ description: html })}
            placeholder="Describe this event..."
            minHeight="120px"
          />
        </div>

        <div>
          <FieldLabel>Featured Image / Thumbnail / Social Share Image</FieldLabel>
          <p className="text-xs text-muted-foreground mb-2">
            Recommended: 1200x600px, max 5MB.{" "}
            <span className="font-semibold text-foreground">
              This image is also used as the social share preview
            </span>{" "}
            when your event link is shared on Facebook, X/Twitter, LinkedIn, iMessage, and other platforms — so choose something eye-catching and on-brand.
          </p>
          {event.thumbnail_url ? (
            <div className="relative inline-block">
              <img
                src={event.thumbnail_url}
                alt="Thumbnail"
                className="w-48 h-24 object-cover rounded-lg border border-border"
              />
              <button
                onClick={() => updateEvent({ thumbnail_url: "" })}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Uploading..." : "Upload Image"}
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
        </div>

        <div>
          <FieldLabel>Cover Photo</FieldLabel>
          <p className="text-xs text-muted-foreground mb-2">
            Wide hero image displayed at the top of the public event page. Recommended: 2400x900px, max 8MB.
          </p>
          {(event as any).cover_photo_url ? (
            <div className="relative inline-block">
              <img
                src={(event as any).cover_photo_url}
                alt="Cover"
                className="w-72 h-28 object-cover rounded-lg border border-border"
              />
              <button
                onClick={() => updateEvent({ cover_photo_url: "" } as any)}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => coverFileInputRef.current?.click()}
              disabled={uploadingCover}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Upload className="w-4 h-4" />
              {uploadingCover ? "Uploading..." : "Upload Cover Photo"}
            </button>
          )}
          <input ref={coverFileInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
        </div>
      </div>

      {/* Event Classification */}
      <SubSection title="Event Classification">
        <div>
          <FieldLabel required>Sport</FieldLabel>
          <select
            value={event.sport_id}
            onChange={(e) => updateEvent({ sport_id: e.target.value })}
            className={inputClass}
          >
            <option value="">Select sport...</option>
            {sports.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <FieldLabel required>Event Type</FieldLabel>
          <select
            value={event.event_type_id}
            onChange={(e) => updateEvent({ event_type_id: e.target.value })}
            className={inputClass}
          >
            <option value="">Select type...</option>
            {eventTypes
              .filter((t) => t.name === "Camp" || t.name === "Tournament")
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name === "Tournament" ? "Event" : t.name}
                </option>
              ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            "Camp" lists this on the Camps page. "Event" lists it on the Events page.
          </p>
        </div>

        <div>
          <FieldLabel>Event Categories</FieldLabel>
          <div className="flex flex-wrap gap-2 mb-2">
            {allCategories.map((cat) => {
              const selected = (event.event_category_ids ?? []).includes(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggleCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected
                      ? "bg-primary text-white border-primary"
                      : "bg-background text-foreground border-border hover:border-primary"
                  }`}
                >
                  {cat.name}
                </button>
              );
            })}
            {allCategories.length === 0 && (
              <p className="text-xs text-muted-foreground">No categories yet — add one below.</p>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
              placeholder="Add new category (e.g. Boys Elite, NCAA Certified)"
              className={`${inputClass} flex-1`}
              disabled={addingCategory}
            />
            <button
              type="button"
              onClick={handleAddCategory}
              disabled={addingCategory || !newCategoryName.trim()}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 hover:opacity-90"
            >
              {addingCategory ? "Adding..." : "+ Add"}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Manage all categories in <a href="/admin-dashboard/event-categories" className="text-primary underline">Event Categories</a>.
          </p>
        </div>
      </SubSection>

      {/* Event Dates */}
      <SubSection title="Event Dates">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Start Date</FieldLabel>
            <input
              type="date"
              value={event.start_date?.split("T")[0] ?? ""}
              onChange={(e) => updateEvent({ start_date: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel>End Date</FieldLabel>
            <input
              type="date"
              value={event.end_date?.split("T")[0] ?? ""}
              onChange={(e) => updateEvent({ end_date: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>Registration Opens</FieldLabel>
            <input
              type="date"
              value={event.registration_open_date?.split("T")[0] ?? ""}
              onChange={(e) => updateEvent({ registration_open_date: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel>Registration Closes</FieldLabel>
            <input
              type="date"
              value={event.registration_close_date?.split("T")[0] ?? ""}
              onChange={(e) => updateEvent({ registration_close_date: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>
      </SubSection>

      {/* More Info */}
      <SubSection title="More Info">
        <div>
          <FieldLabel>More Info Text</FieldLabel>
          <p className="text-xs text-muted-foreground mb-1">Renders as a blue info box on the public page</p>
          <textarea
            value={event.more_info}
            onChange={(e) => updateEvent({ more_info: e.target.value })}
            placeholder="Additional information for attendees..."
            rows={3}
            className={inputClass + " resize-none"}
          />
        </div>
      </SubSection>
    </div>
  );
}